'use client';

import { FC, useEffect, useState, useLayoutEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Home, FileText, Settings, RotateCcw, ExternalLink, Loader2 } from 'lucide-react';

interface MenuItem {
  name: string;
  href: string;
  icon: FC<React.SVGProps<SVGSVGElement>>;
  isReloadButton?: boolean;
  openInNewTab?: boolean;
}

// メニュー項目を定義（メインナビゲーションと設定を分離）
const mainMenuItems: MenuItem[] = [
  { name: 'App', href: '/', icon: Home, isReloadButton: true },
  { name: 'Reports', href: '/reports', icon: FileText },
];

const settingsMenuItem: MenuItem = { 
  name: 'Settings', 
  href: '/settings', 
  icon: Settings, 
  openInNewTab: true 
};

const Menu: FC = () => {
  const [mounted, setMounted] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ 
    transform: 'translateY(0px)',
    opacity: 0 
  });
  
  const pathname = usePathname();
  const router = useRouter();
  const navRef = useRef<HTMLElement>(null);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  // レポートページの遷移処理
  const handleReportsClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setLoadingReports(true);
    
    // プリフェッチを実行
    router.prefetch('/reports');
    
    // 即座にローディング状態を表示
    setTimeout(() => {
      router.push('/reports');
    }, 100);
  };

  // マウスホバー時のプリフェッチ処理
  const handleReportsHover = () => {
    // reportsページをプリフェッチ
    router.prefetch('/reports');
  };

  // パスが変更されたらローディング状態をリセット
  useEffect(() => {
    setLoadingReports(false);
  }, [pathname]);

  // マウント状態を管理してハイドレーションエラーを防ぐ
  useEffect(() => {
    setMounted(true);
    
    // ログインページ以外でのみスライドインアニメーションを実行
    if (pathname !== '/login' && !hasAnimated) {
      const timer = setTimeout(() => {
        setIsVisible(true);
        setHasAnimated(true);
      }, 300);

      return () => clearTimeout(timer);
    } else if (hasAnimated) {
      setIsVisible(true);
    }
  }, [pathname, hasAnimated]);

  // ダークモード検出とリスナー設定
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setIsDarkMode(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setIsDarkMode(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // アクティブなアイテムのインデックスを取得（メインメニューのみ）
  const activeIndex = mainMenuItems.findIndex(item => pathname === item.href);

  // デスクトップ用：インジケータの位置を計算してアニメーション
  useLayoutEffect(() => {
    if (!mounted || activeIndex === -1 || !navRef.current) return;

    const indicatorOffset = 8 + (48 + 12) * activeIndex;

    setIndicatorStyle({
      transform: `translateY(${indicatorOffset}px)`,
      opacity: 1
    });
  }, [mounted, activeIndex]);

  // モバイル用：インジケータの位置を計算（メインメニューのみ）
  const getMobileIndicatorStyle = () => {
    if (!mounted || activeIndex === -1) return { transform: 'translateX(0px)', opacity: 0 };
    
    const indicatorOffset = 8 + (48 + 12) * activeIndex;
    
    return {
      transform: `translateX(${indicatorOffset}px)`,
      opacity: 1
    };
  };

  // ページ再読み込み処理
  const handleReload = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.location.reload();
  };

  // 別タブで開く処理
  const handleNewTab = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  // ログインページまたはダッシュボードページでは何も表示しない（Hooksの後に配置）
  if (pathname === '/login' || pathname === '/settings') {
    return null;
  }

  // マウント前は何も表示しない
  if (!mounted) {
    return null;
  }
  
  // デスクトップ用のスタイリング
  const glassmorphismNav = isDarkMode
    ? "bg-stone-700/70 backdrop-blur-md border-stone-900/10 shadow-xl shadow-black/25"
    : "bg-white backdrop-blur-md border-black/10 shadow-xl shadow-black/10";

  const textColors = isDarkMode
    ? { active: "text-gray-900", inactive: "text-gray-300 hover:text-white" }
    : { active: "text-gray-100", inactive: "text-gray-600 hover:text-gray-900" };

  const indicatorBg = isDarkMode ? "bg-white/90" : "bg-black/90";

  const tooltipBg = isDarkMode 
    ? "bg-stone-800/95 text-white border-stone-700/50" 
    : "bg-white/95 text-gray-900 border-gray-200/50";

  return (
    <>
      {/* デスクトップ表示：左側固定・menu自体は常に中央 */}
      <menu className={`
        hidden md:flex fixed left-6 top-1/2 -translate-y-1/2 z-100 
        flex-col gap-4 transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-full'}
      `}>
        {/* メインナビゲーションセクション */}
        <nav 
          ref={navRef}
          className={`relative flex flex-col rounded-2xl p-2 gap-3 ${glassmorphismNav}`}
          aria-label="メインナビゲーション"
        >
          
          {/* インジケータ */}
          <div
            className={`
              absolute w-12 h-12 rounded-xl shadow-sm transition-all duration-300 ease-out
              ${indicatorBg}
            `}
            style={{
              ...indicatorStyle,
              top: '0px',
            }}
          />

          {/* メインメニューアイテム */}
          {mainMenuItems.map((item, index) => {
            const isActive = pathname === item.href;
            const isReports = item.href === '/reports';
            const showLoader = isReports && loadingReports;
            
            // 現在のページと同じhrefの場合は再読み込みアイコンを使用
            let Icon = pathname === item.href ? RotateCcw : item.icon;
            
            // レポートページローディング中はローダーアイコンを表示
            if (showLoader) {
              Icon = Loader2;
            }
            
            const currentTextColors = textColors;
            const currentTooltipBg = tooltipBg;

            return (
              <div key={item.href} className="relative group">
                <Link
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  href={item.href}
                  onClick={
                    pathname === item.href
                      ? handleReload 
                      : isReports 
                        ? handleReportsClick 
                        : undefined
                  }
                  className={`
                    relative flex items-center justify-center
                    w-12 h-12 rounded-xl
                    transition-all duration-200 ease-out
                    hover:bg-slate-900/20 hover:backdrop-blur-sm
                    hover:scale-105 active:scale-95
                    ${isActive
                      ? `${currentTextColors.active} z-10`
                      : currentTextColors.inactive
                    }
                    ${showLoader ? 'cursor-wait' : ''}
                  `}
                  aria-label={
                    showLoader 
                      ? 'Loading Reports...' 
                      : pathname === item.href
                        ? 'Reload Page' 
                        : item.name
                  }
                  onMouseEnter={() => {
                    setHoveredIndex(index);
                    if (isReports) {
                      handleReportsHover();
                    }
                  }}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <Icon 
                    width={20}
                    height={20}
                    strokeWidth={isActive ? 2 : 1.5}
                    className={`transition-all duration-200 ${showLoader ? 'animate-spin' : ''}`}
                  />
                </Link>

                {/* ツールチップ */}
                <div
                  className={`
                    absolute left-16 top-1/2 -translate-y-1/2 z-50
                    px-3 py-2 rounded-lg text-sm font-medium
                    ${currentTooltipBg} backdrop-blur-md border
                    shadow-lg transition-all duration-200 ease-out
                    pointer-events-none whitespace-nowrap
                    ${hoveredIndex === index 
                      ? 'opacity-100 translate-x-0' 
                      : 'opacity-0 -translate-x-2'
                    }
                  `}
                >
                  {showLoader 
                    ? 'Loading Reports...' 
                    : pathname === item.href
                      ? 'Reload Page' 
                      : item.name
                  }
                  <div 
                    className={`
                      absolute right-full top-1/2 -translate-y-1/2
                      border-4 border-transparent
                      ${isDarkMode ? 'border-r-stone-800/95' : 'border-r-white/95'}
                    `}
                  />
                </div>
              </div>
            );
          })}
        </nav>

        {/* 設定セクション（分離） */}
        <nav 
          className={`relative flex flex-col rounded-2xl p-2 ${glassmorphismNav}`}
          aria-label="設定ナビゲーション"
        >
          <div className="relative group">
            <Link
              href={settingsMenuItem.href}
              onClick={(e) => handleNewTab(e, settingsMenuItem.href)}
              className={`
                relative flex items-center justify-center
                w-12 h-12 rounded-xl
                transition-all duration-200 ease-out
                hover:bg-slate-900/20 hover:backdrop-blur-sm
                hover:scale-105 active:scale-95
                ${textColors.inactive}
              `}
              aria-label={`${settingsMenuItem.name} (新しいタブで開く)`}
              onMouseEnter={() => setHoveredIndex(100)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <settingsMenuItem.icon 
                width={20}
                height={20}
                strokeWidth={1.5}
                className="transition-all duration-200"
              />
            </Link>

            {/* 設定ツールチップ */}
            <div
              className={`
                absolute left-16 top-1/2 -translate-y-1/2 z-50
                px-3 py-2 rounded-lg text-sm font-medium
                ${tooltipBg} backdrop-blur-md border
                shadow-lg transition-all duration-200 ease-out
                pointer-events-none whitespace-nowrap
                ${hoveredIndex === 100 
                  ? 'opacity-100 translate-x-0' 
                  : 'opacity-0 -translate-x-2'
                }
              `}
            >
              <div className="flex items-center gap-2">
                <span>{settingsMenuItem.name}</span>
                <ExternalLink 
                  width={14}
                  height={14}
                  strokeWidth={1.5}
                  className="opacity-70"
                />
              </div>
              <div 
                className={`
                  absolute right-full top-1/2 -translate-y-1/2
                  border-4 border-transparent
                  ${isDarkMode ? 'border-r-stone-800/95' : 'border-r-white/95'}
                `}
              />
            </div>
          </div>
        </nav>
      </menu>

      {/* モバイル表示：下部固定・横並び */}
      <menu className={`
        md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-100 
        transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}
      `}>
        <div className="flex gap-2">
          <nav 
            className={`relative flex ${glassmorphismNav} rounded-2xl p-2 gap-3`}
            aria-label="モバイルナビゲーション"
          >
            {/* インジケータ */}
            <div
              className={`absolute left-0 w-12 h-12 ${indicatorBg} rounded-xl shadow-sm transition-all duration-300 ease-out`}
              style={{
                ...getMobileIndicatorStyle(),
                top: '8px',
              }}
            />

            {/* メインメニューアイテム */}
            {mainMenuItems.map((item) => {
              const isActive = pathname === item.href;
              const isReports = item.href === '/reports';
              const showLoader = isReports && loadingReports;
              
              // 現在のページと同じhrefの場合は再読み込みアイコンを使用
              let Icon = pathname === item.href ? RotateCcw : item.icon;
              
              // レポートページローディング中はローダーアイコンを表示
              if (showLoader) {
                Icon = Loader2;
              }

              return (
                <div key={item.href} className="relative group">
                  <Link
                    href={item.href}
                    onClick={
                      pathname === item.href
                        ? handleReload 
                        : isReports 
                          ? handleReportsClick 
                          : undefined
                    }
                    className={`
                      relative flex items-center justify-center
                      w-12 h-12 rounded-xl
                      transition-all duration-200 ease-out
                      hover:bg-slate-900/20 hover:backdrop-blur-sm
                      hover:scale-105 active:scale-95
                      ${isActive
                        ? `${textColors.active} z-10`
                        : textColors.inactive
                      }
                      ${showLoader ? 'cursor-wait' : ''}
                    `}
                    aria-label={
                      showLoader 
                        ? 'Loading Reports...' 
                        : pathname === item.href
                          ? 'Reload Page' 
                          : item.name
                    }
                  >
                    <Icon 
                      width={20}
                      height={20}
                      strokeWidth={isActive ? 2 : 1.5}
                      className={`transition-all duration-200 ${showLoader ? 'animate-spin' : ''}`}
                    />
                  </Link>
                </div>
              );
            })}
          </nav>

          {/* 設定セクション（分離） */}
          <nav className={`relative flex ${glassmorphismNav} rounded-2xl p-2`}>
            <div className="relative group">
              <Link
                href={settingsMenuItem.href}
                onClick={(e) => handleNewTab(e, settingsMenuItem.href)}
                className={`
                  relative flex items-center justify-center
                  w-12 h-12 rounded-xl
                  transition-all duration-200 ease-out
                  hover:bg-slate-900/20 hover:backdrop-blur-sm
                  hover:scale-105 active:scale-95
                  ${textColors.inactive}
                `}
                aria-label={`${settingsMenuItem.name} (新しいタブで開く)`}
              >
                <settingsMenuItem.icon 
                  width={20}
                  height={20}
                  strokeWidth={1.5}
                  className="transition-all duration-200"
                />
              </Link>
            </div>
          </nav>
        </div>
      </menu>
    </>
  );
};

export default Menu;